import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import { APPEAL_TYPES, EVENT_TYPES, programAreaMap } from '../constants';
import {
  buildSafeDateFormatter,
  getAppealStatusTitle,
  getFailedSubmissionsWithinLast30Days,
  getListItemLabel,
  getTypeName,
} from '../utils';

import CardLink from './CardLink';

const formatDate = buildSafeDateFormatter();

const capitalizeWord = word => {
  if (!word) return '';
  return word[0].toUpperCase() + word.slice(1);
};

export default function AppealContent({ appeal, onLinkClick }) {
  const appealStatusTitle = getAppealStatusTitle(appeal);
  let requestEventType;
  let isAppeal;

  switch (appeal.type) {
    case APPEAL_TYPES.legacy:
      requestEventType = EVENT_TYPES.nod;
      isAppeal = true;
      break;
    case APPEAL_TYPES.supplementalClaim:
      requestEventType = EVENT_TYPES.scRequest;
      isAppeal = false;
      break;
    case APPEAL_TYPES.higherLevelReview:
      requestEventType = EVENT_TYPES.hlrRequest;
      isAppeal = false;
      break;
    case APPEAL_TYPES.appeal:
      requestEventType = EVENT_TYPES.amaNod;
      isAppeal = true;
      break;
    default:
      requestEventType = null;
      isAppeal = false;
  }

  // `events` is contractually allowed to be empty per the Caseflow schema
  // (no minItems on `events`). Guard the lookups so the card doesn't crash
  // on a valid-but-bare appeal.
  const events = appeal.attributes.events || [];
  const requestEvent = events.find(event => event.type === requestEventType);
  const updatedEventDateString = events[events.length - 1]?.date;
  const programArea = programAreaMap[appeal.attributes.programArea];

  let appealTitle = getTypeName(appeal);

  if (programArea) {
    if (isAppeal) {
      appealTitle = `${programArea} ${appealTitle}`;
    } else {
      appealTitle += ` for ${programArea}`;
    }
  }
  appealTitle = capitalizeWord(appealTitle);

  const failedSubmissionsWithinLast30Days = useMemo(
    () =>
      getFailedSubmissionsWithinLast30Days(
        appeal.attributes?.evidenceSubmissions || [],
      ),
    [appeal.attributes?.evidenceSubmissions],
  );
  const showActionTag = failedSubmissionsWithinLast30Days.length > 0;

  const label = getListItemLabel(appeal);
  const updatedOn = formatDate(updatedEventDateString);
  const formattedReceivedDate = formatDate(requestEvent?.date);
  const ariaLabel = `Details for ${appealTitle}`;
  const href = `/track-claims/appeals/${appeal.id}/status`;

  return (
    <>
      <h3 className="claim-list-item-header vads-u-margin-bottom--2">
        <div>
          {label && (
            <>
              <span className="usa-label">{label}</span>{' '}
            </>
          )}
          {appealTitle}
          {formattedReceivedDate && (
            <span className="vads-u-margin-top--0p5 submitted-on">
              {`Received on ${formattedReceivedDate}`}
            </span>
          )}
        </div>
      </h3>
      <div className="card-status">
        {appeal.attributes.description && (
          <p>
            {appeal.attributes.issues?.length === 1 ? 'Issue' : 'Issues'} on
            {isAppeal ? ' appeal' : ' review'}:{' '}
            <span
              className="masked-issue"
              data-dd-privacy="mask"
              data-dd-action-name="appeal issue"
            >
              {appeal.attributes.description}
            </span>
          </p>
        )}
        {appealStatusTitle && (
          <p>
            Status:{' '}
            <span
              className="masked-issue"
              data-dd-privacy="mask"
              data-dd-action-name="appeal issue"
            >
              {appealStatusTitle}
            </span>
          </p>
        )}
        {updatedOn && <p>Last updated: {updatedOn}</p>}
        {showActionTag && (
          <va-tag-status status="warning" text="Action may be needed" />
        )}
      </div>
      <CardLink ariaLabel={ariaLabel} href={href} onLinkClick={onLinkClick} />
    </>
  );
}

AppealContent.propTypes = {
  appeal: PropTypes.object.isRequired,
  onLinkClick: PropTypes.func,
};
